import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, css, fonts, frames, glyphs, useMediaQuery } from '../../../../Styles'
import { BasicInfo, Events, Feed, MainContentPaddingTypes, Marking, NewModal, PageHeader, Timeline } from '../../../../Components'
import { ButtonTypes } from '../../../../Definitions'

import { useLocation } from 'react-router'
import { timelineTypes } from '../../../../Components/Animals/timeline'
import { useAppSelector } from '../../../../Store'
import { Graph_QL } from '../../../../Services'
import { useAnimal } from '../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal'
import { Pages } from '../../../../Navigation/root'
import { useAnimalOptions } from '../../../../Components/_Components'
import NewButton, { NewButtonList } from '../../../../Components/BaseComponents/newButton'

export default function Animal() {
  const {t} = useTranslation();
  let location = useLocation();
  const [selectedAnimal, setAnimal] = useState<number>(0)
  const [showTimelineModal, setShowTimelineModal] = useState<boolean>(false)
  const [feedView, setFeedView] = useState<boolean>(true)
  const [markingView, setMarkingView] = useState<boolean>(false)
  const [eventsView, setEventsView] = useState<boolean>(false)
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<boolean>(false)

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const { call: getAnimalsInfo, data: animalsState, maintainEntries: updateAnimal } = useAnimal(connection);
 
  useEffect(() => {
    if (!(animalsState[domain])) {
      getAnimalsInfo({fetchAmount: 50}, connection);
    }
  },[animalsState, connection, domain, getAnimalsInfo])

  useEffect(() => {
    if (selectedAnimal && selectedAnimal !== 0) {
      getAnimalsInfo({ids:[selectedAnimal], ifFeedCurveBaseInfoOnly: false, ifSkipLastTenDaysConsumption: false}, connection)
    }
  }, [connection, getAnimalsInfo, selectedAnimal])

  // #region | Mediaqueries
  const vertical = useMediaQuery(`(max-width: 740px), (min-width: 1000px) and (max-width: 1060px)`);
  const verticalTimelineModal = useMediaQuery(`(max-width: 745px)`);
  const hideBtns = useMediaQuery(`(max-width: 600px)`);
  const hideViewMenu = useMediaQuery(`(max-width: 500px), (min-width: 741px) and (max-width: 880px), (min-width: 1060px) and (max-width: 1195px)`);
  const heightMin = useMediaQuery(`(min-height: 910px)`);
  const heightMax = useMediaQuery(`(max-height: 785px)`);
  // #endregion

  const animalOptions = useAnimalOptions();
  
  // Safe initialization logic
  useEffect(() => {
    if (!selectedAnimal) {
      if (location.state?.params?.selected) {
        setAnimal(location.state.params.selected);
      } else if (animalOptions?.length) {
        setAnimal(animalOptions[0].id);
      }
    }
  }, [selectedAnimal, animalOptions, location.state?.params?.selected]);

  // #region | Viewer for Feed, Marking, Events...
  const viewer = 
    <div style={{height: "100%", width: "100%", display: "grid", gridTemplateColumns: "70px 1fr", gridTemplateRows: "50px calc(100% - 50px)"}}>
      {/* Buttons */}
      <div style={{ gridArea: "1/1/2/2", position: "relative", zIndex: "99", backgroundColor: "transparent", display: "flex", justifyContent: "start", padding: "0px 20px", gap: "5px"}}>
        <NewButtonList 
          disabled={[false, false]}
          contents={[
            t("feed.general.feed", {defaultValue: "feed"}), 
            // t("animalPages.animal.marking", {defaultValue: "Marking"}), 
            t("animalPages.animal.events", {defaultValue: "events"})
          ]} 
          onClick={(i => {
            if (i === 0) { setFeedView(true); setMarkingView(false); setEventsView(false)}
            // else if (i === 1) { setMarkingView(true); setFeedView(false); setEventsView(false)}
            else if (i === 1) { setEventsView(true); setFeedView(false); setMarkingView(false)}
          })}
          overrides={{
            baseStyles: { borderRadius: hideViewMenu ? "8px" : "8px 8px 0px 0px", padding: hideViewMenu ? "5px" : "0px 5px 2px 5px", backgroundColor: hideViewMenu ? colors.transparent : colors.greySecondary },
            hoverStyles: { backgroundColor: hideViewMenu ? colors.greyDarkSecondary : colors.greyPrimary }
          }}
          buttonType={ButtonTypes.tableButton}
          startEnhancers={[
            glyphs.FeedPlan({}), 
            // glyphs.Animal({}), 
            glyphs.Calendar({})]}
          listOverrides={{
            listGap: hideViewMenu ? "10px" : "5px"
          }}
          popOverrides={{
            hasPop: hideViewMenu ? true : false,
            popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "10px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d", right: "unset", top: "-10px", left: "55px"},
            buttonType: ButtonTypes.tableButton,
            openContent: "Menu",
            closeContent: "Menu",
            closeEnhancer: <div style={{marginBottom: "-4px"}}>{glyphs.Close({})}</div>,
            openEnhancer: <>{glyphs.Animal({})}</>
          }}
        />
      </div>
      {/* Content */}
        { feedView && <div style={{gridArea: "1/1/3/3", overflow: "hidden"}}><Feed selectedAnimal={selectedAnimal} connect={{connection: connection, domain: domain}} /></div> }
        { markingView && <div style={{gridArea: "1/1/3/3", overflow: "hidden"}}><Marking selectedAnimal={selectedAnimal}/></div> }
        { eventsView && <div style={{gridArea: "1/1/3/3", overflow: "hidden"}}><Events selectedAnimal={selectedAnimal}/></div> }

    </div>
  // #endregion
  
  return (
    <frames.Page_inner>
      <PageHeader
        topHeader={t(`menuHeaders.${Pages.animals}`, { defaultValue: "animals" })}
        optionButtons={{
          contents: heightMax || vertical
            ? [t("animalPages.animal.viewTimeline", { defaultValue: "View Timeline" }), t("animalPages.animal.deleteAnimal", { defaultValue: "delete animal" })]
            : [t("animalPages.animal.deleteAnimal", { defaultValue: "delete animal"})],
          onClick: (index) => {
            if (heightMax || vertical) {
              if (index === 0) { setShowTimelineModal(true)}
              if (index === 1) { setConfirmDeleteModal(true)}
            } else {
              if (index === 0) { setConfirmDeleteModal(true) }
            }
          }
        }}
        subSelect={{
          options: animalOptions,
          defaultValue: animalOptions.find(animal => animal.id === selectedAnimal),
          onChange: (e) => { setAnimal(e?.id ?? location.state.params.selected) }
        }}
      />
      <frames._page_inner_content>
        <div style={{
          height: "100%",
          display: "grid", 
          gridTemplateColumns: vertical ? "1fr" : "360px 1fr", 
          gridTemplateRows: vertical ? "360px 1fr" : heightMin ? "1fr 360px" : heightMax ? "100%" : "300px calc(100% - 300px)", 
          columnGap: vertical ? "0px" : "20px", 
          rowGap: vertical ? "20px" : "0px", 
          overflowY: vertical ? "scroll" : "hidden", 
          scrollbarWidth: "thin", 
          scrollbarColor: colors.greyDarkPrimary+colors.transparent
        }}>
          {(heightMax || vertical)
            ?  <Timeline selectedAnimal={selectedAnimal} timelineType={verticalTimelineModal ? timelineTypes.verticalModal : timelineTypes.horizontalModal} isModalOpen={!!showTimelineModal} closeModal={() => setShowTimelineModal(false)}/>
            : <div style={{gridArea: "1/1/2/3", display: "flex", alignItems: "center", justifyContent: "center"}}>
                <Timeline selectedAnimal={selectedAnimal} timelineType={timelineTypes.primary}/>
              </div>
          }
          <div style={{gridArea: vertical ? "1/1/2/2" : heightMax ? "1/1/2/2" : "2/1/3/2"}}><BasicInfo selectedAnimal={selectedAnimal}/></div>
          <div style={{gridArea: vertical ? "2/1/3/2" : heightMax ? "1/2/2/3" : "2/2/3/3"}}>{viewer}</div>
        </div>    

        <NewModal
          modalState={confirmDeleteModal}
          header={"Delete Animal"}
          overrides={{
            mainContentPadding: MainContentPaddingTypes.secondary,
            overlayColor: colors.blackPrimary+"99"
          }}
          mainContent={ 
            <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px"}}>
              <p style={{...fonts.fontSize3, ...fonts.paragraph, color: colors.greyLightTertiary}}>Are you sure you want to delete this Animal?</p>
              <NewButton
                content={"confirm"}
                onClick={() => { setConfirmDeleteModal(false); if (updateAnimal) { updateAnimal(undefined, { data: [{ animal_id: selectedAnimal, deleted: true, location: null, feedcurve_id: null, fk_feedcurve_milestones: null, milestone_day: null  }] }) } setAnimal(animalOptions[((animalOptions.findIndex(a => a.id === selectedAnimal))+1)%animalOptions.length].id)}}
              />
              <NewButton
                content={"cancel"}
                onClick={() => setConfirmDeleteModal(false)}
                buttonType={ButtonTypes.textButton}
                overrides={{
                  contentStyles: {...css.button_TextUnderline, whiteSpace: "nowrap"}
                }}
              />
            </div>
          }
        />
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}